import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

/**
 * Deploy refresh (staging dogfooding → launch): the SPA is cached in an
 * open tab, so a redeploy leaves the user on the old bundle with no
 * signal. This polls the served index.html for a new asset hash and
 * offers a one-click refresh; a dismissed notice stays dismissed for
 * that build. Checks run when the tab is visible (including immediately
 * on return from background), never while the user is away.
 */
const FILE_RE = /index-[A-Za-z0-9_-]+\.js/;

function runningAsset(): string | null {
  const el = document.querySelector<HTMLScriptElement>(
    'script[src*="/assets/index-"]',
  );
  return el?.src.match(FILE_RE)?.[0] ?? null;
}

async function servedAsset(): Promise<string | null> {
  const res = await fetch("/index.html", { cache: "no-store" });
  if (!res.ok) return null;
  return (await res.text()).match(FILE_RE)?.[0] ?? null;
}

export function DeployRefresh({
  intervalMs = 120_000,
}: {
  intervalMs?: number;
}) {
  const [nextAsset, setNextAsset] = useState<string | null>(null);
  const dismissed = useRef<string | null>(null);

  const check = useCallback(async () => {
    if (document.visibilityState !== "visible") return;
    const [running, served] = [runningAsset(), await servedAsset()];
    if (
      served &&
      running &&
      served !== running &&
      served !== dismissed.current
    ) {
      setNextAsset(served);
    }
  }, []);

  useEffect(() => {
    void check();
    const timer = window.setInterval(() => void check(), intervalMs);
    const onVisible = () => void check();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check, intervalMs]);

  if (!nextAsset) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-line-100 bg-bg-200 px-4 py-2 shadow-popover"
    >
      <span className="text-sm text-ink-100">
        A new version of corenote is ready.
      </span>
      <Button
        size="sm"
        onClick={() => window.location.reload()}
        aria-label="Refresh to the new version"
      >
        Refresh
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          dismissed.current = nextAsset;
          setNextAsset(null);
        }}
        aria-label="Keep this version for now"
      >
        Later
      </Button>
    </div>
  );
}
